<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { moveApi } from '../services/moveApi'
import { productionApi } from '../services/productionApi'
import { mapTaskFromApi } from '../utils/moveData'
import { formatDateTime } from '../utils/productionDates'
import { productionProgressSteps } from '../config/productionNavigation'
import AppLayout from '../layouts/AppLayout.vue'
import ProductionAttentionList from './production/ProductionAttentionList.vue'
import { useMoveTheme } from '../composables/useMoveTheme'
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js'
import { Pie, Bar } from 'vue-chartjs'

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement)

const router = useRouter()
const store = useStore()
const { isDark } = useMoveTheme()

const error = ref('')
const loading = ref(true)
const stats = ref({ totalBoards: 0, totalLists: 0, totalTasks: 0, productions: 0, openGaps: 0 })
const ownedBoards = ref(0)
const sharedBoards = ref(0)
const hasBoardChartData = ref(false)
const hasTaskChartData = ref(false)
const myTasks = ref([])
const productionStats = ref({
  productions: 0,
  productions_with_gaps: 0,
  open_gaps: 0,
  needs_attention: [],
  upcoming_shoots: [],
  productions_by_status: {}
})

const hasProductionAccess = computed(() => store.getters['auth/hasProductionAccess'])

const firstName = computed(() => {
  const name = store.getters['auth/displayName'] || ''
  return name.split(/\s+/).filter(Boolean)[0] || ''
})

const welcomeTitle = computed(() => (
  firstName.value ? `Welcome back, ${firstName.value}` : 'Welcome back'
))

const byStatus = computed(() => productionStats.value.productions_by_status || {})
const needsAttention = computed(() => productionStats.value.needs_attention || [])
const upcomingShoots = computed(() => productionStats.value.upcoming_shoots || [])

const statsList = computed(() => {
  if (hasProductionAccess.value) {
    return [
      { label: 'Boards', key: 'totalBoards', icon: 'view_kanban', variant: 'primary', to: '/boards' },
      { label: 'Productions', key: 'productions', icon: 'movie_filter', variant: 'secondary', to: '/production/productions' },
      { label: 'Tasks', key: 'totalTasks', icon: 'task_alt', variant: 'tertiary', to: '/my-tasks' },
      { label: 'Missing items', key: 'openGaps', icon: 'error_outline', variant: 'secondary', to: '/production/productions' }
    ]
  }
  return [
    { label: 'Boards', key: 'totalBoards', icon: 'view_kanban', variant: 'primary', to: '/boards' },
    { label: 'Lists', key: 'totalLists', icon: 'view_list', variant: 'secondary', to: '/boards' },
    { label: 'Tasks', key: 'totalTasks', icon: 'task_alt', variant: 'tertiary', to: '/my-tasks' }
  ]
})

const statsGridClass = computed(() => (
  hasProductionAccess.value
    ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5'
    : 'grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5'
))

const quickActions = computed(() => {
  const actions = [
    { label: 'Create board', route: '/boards/create', icon: 'add' },
    { label: 'View boards', route: '/boards', icon: 'view_kanban' },
    { label: 'My tasks', route: '/my-tasks', icon: 'task_alt' }
  ]
  if (hasProductionAccess.value) {
    actions.push(
      { label: 'Productions', route: '/production/productions', icon: 'theaters' },
      { label: 'Projects', route: '/production/projects', icon: 'folder_special' }
    )
  }
  return actions
})

const focusTasks = computed(() => {
  const rank = { overdue: 0, today: 1, soon: 2, later: 3, none: 4 }
  return myTasks.value
    .filter((task) => !task.archived)
    .map((task) => ({ ...task, dueState: dueState(task.dueDate) }))
    .sort((a, b) => {
      const byState = rank[a.dueState] - rank[b.dueState]
      if (byState !== 0) return byState
      const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Number.POSITIVE_INFINITY
      const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Number.POSITIVE_INFINITY
      return aTime - bTime
    })
    .slice(0, 6)
})

const overdueTaskCount = computed(() => (
  focusTasks.value.filter((task) => task.dueState === 'overdue').length
))

const directionLine = computed(() => {
  const parts = []
  const productionsNeeding = productionStats.value.productions_with_gaps || 0
  if (hasProductionAccess.value && productionsNeeding) {
    parts.push(`${productionsNeeding} ${productionsNeeding === 1 ? 'production needs' : 'productions need'} missing pieces`)
  }
  if (overdueTaskCount.value) {
    parts.push(`${overdueTaskCount.value} overdue ${overdueTaskCount.value === 1 ? 'task' : 'tasks'}`)
  } else if (focusTasks.value.length) {
    parts.push(`${focusTasks.value.length} ${focusTasks.value.length === 1 ? 'task' : 'tasks'} on your list`)
  }
  if (!parts.length) {
    return hasProductionAccess.value
      ? 'Boards, tasks, and productions in one place. You are caught up — pick a board or a production to keep going.'
      : 'Track boards, lists, and tasks across your team. Start with what is due.'
  }
  return `Start here: ${parts.join(' · ')}.`
})

const productionEmptyTitle = computed(() => (
  (productionStats.value.productions || 0) === 0
    ? 'No productions yet'
    : 'Nothing blocking a shoot'
))

const productionEmptyBody = computed(() => (
  (productionStats.value.productions || 0) === 0
    ? 'Create a production under a project to track talent, scenes, mood boards, and shoot days.'
    : 'Every production has the people and pieces it needs. Open productions to keep going.'
))

const chartTextColor = computed(() => isDark.value ? 'rgba(245, 251, 248, 0.75)' : 'rgba(15, 36, 25, 0.65)')
const chartGridColor = computed(() => isDark.value ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 36, 25, 0.08)')

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { color: chartTextColor.value, padding: 16, usePointStyle: true }
    }
  },
  scales: {
    x: {
      ticks: { color: chartTextColor.value },
      grid: { color: chartGridColor.value }
    },
    y: {
      beginAtZero: true,
      ticks: { color: chartTextColor.value, precision: 0 },
      grid: { color: chartGridColor.value }
    }
  }
}))

const pieOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { color: chartTextColor.value, padding: 16, usePointStyle: true }
    }
  }
}))

const boardChartData = ref({
  labels: ['My boards', 'Shared with me'],
  datasets: [{
    data: [0, 0],
    backgroundColor: ['#00E181', '#3b82f6'],
    borderColor: isDark.value ? '#0a0f0d' : '#ffffff',
    borderWidth: 2
  }]
})

const taskChartData = ref({
  labels: [],
  datasets: [{
    label: 'Tasks',
    data: [],
    backgroundColor: '#00E181',
    borderColor: '#00c96e',
    borderWidth: 1,
    borderRadius: 8
  }]
})

function dueState (dueDate) {
  if (!dueDate) return 'none'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(dueDate)
  if (Number.isNaN(due.getTime())) return 'none'
  due.setHours(0, 0, 0, 0)
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24))
  if (diffDays < 0) return 'overdue'
  if (diffDays === 0) return 'today'
  if (diffDays <= 7) return 'soon'
  return 'later'
}

function formatTaskDue (dueDate) {
  if (!dueDate) return 'No due date'
  const date = new Date(dueDate)
  if (Number.isNaN(date.getTime())) return 'No due date'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const due = new Date(date)
  due.setHours(0, 0, 0, 0)
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Due today'
  if (diffDays === 1) return 'Due tomorrow'
  if (diffDays === -1) return 'Due yesterday'
  if (diffDays < 0) return `Overdue · ${date.toLocaleDateString()}`
  if (diffDays <= 7) return `Due in ${diffDays}d`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const fetchStats = async () => {
  try {
    loading.value = true
    error.value = ''

    const [statsResponse, boardsResponse, tasksResponse, productionResponse] = await Promise.all([
      moveApi.dashboardStats(),
      moveApi.listBoards(),
      moveApi.myTasks().catch(() => ({ data: { data: [] } })),
      hasProductionAccess.value
        ? productionApi.dashboard().catch(() => ({ data: {} }))
        : Promise.resolve({ data: {} })
    ])

    const dashboard = statsResponse.data.data
    const userBoards = boardsResponse.data.data || []
    myTasks.value = (tasksResponse.data.data || []).map(mapTaskFromApi)
    productionStats.value = productionResponse.data || {}

    stats.value = {
      totalBoards: dashboard.total_boards || 0,
      totalLists: dashboard.total_lists || 0,
      totalTasks: dashboard.total_tasks || 0,
      productions: productionStats.value.productions || 0,
      openGaps: productionStats.value.open_gaps || 0
    }

    ownedBoards.value = dashboard.owned_boards || 0
    sharedBoards.value = dashboard.shared_boards || 0
    hasBoardChartData.value = ownedBoards.value + sharedBoards.value > 0

    boardChartData.value = {
      labels: ['My boards', 'Shared with me'],
      datasets: [{
        data: [ownedBoards.value, sharedBoards.value],
        backgroundColor: ['#00E181', '#3b82f6'],
        borderColor: isDark.value ? '#0a0f0d' : '#ffffff',
        borderWidth: 2
      }]
    }

    const tasksPerBoard = await Promise.all(userBoards.map(async (board) => {
      try {
        const { data } = await moveApi.getBoard(board.id)
        return {
          boardName: board.name || 'Unnamed board',
          taskCount: (data.data.tasks || []).length
        }
      } catch {
        return { boardName: board.name || 'Unnamed board', taskCount: 0 }
      }
    }))

    tasksPerBoard.sort((a, b) => b.taskCount - a.taskCount)
    hasTaskChartData.value = tasksPerBoard.some(item => item.taskCount > 0)

    taskChartData.value = {
      labels: tasksPerBoard.map(item => item.boardName),
      datasets: [{
        label: 'Tasks',
        data: tasksPerBoard.map(item => item.taskCount),
        backgroundColor: '#00E181',
        borderColor: '#00c96e',
        borderWidth: 1,
        borderRadius: 8
      }]
    }
  } catch (err) {
    console.error('Error fetching stats:', err)
    error.value = 'Failed to load dashboard statistics. Try refreshing.'
  } finally {
    loading.value = false
  }
}

onMounted(fetchStats)
</script>

<template>
  <AppLayout>
    <div class="max-w-6xl mx-auto space-y-8">
      <section class="move-card p-6 md:p-8 overflow-hidden relative">
        <div
          class="absolute inset-0 opacity-40 pointer-events-none"
          style="background: radial-gradient(circle at top right, var(--move-accent-muted), transparent 55%)"
        />
        <div class="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div class="text-left">
            <p class="text-xs font-bold tracking-[0.18em] uppercase move-text-faint mb-2">Move</p>
            <h2 class="text-2xl md:text-3xl font-extrabold move-text mb-2">{{ welcomeTitle }}</h2>
            <p class="move-text-muted max-w-xl">
              {{ directionLine }}
            </p>
          </div>
          <div class="flex flex-wrap gap-3 shrink-0 self-start md:self-auto">
            <router-link to="/boards/create" class="move-btn-primary">
              <span class="material-icons text-lg">add</span>
              New board
            </router-link>
            <router-link
              v-if="hasProductionAccess"
              to="/production/productions"
              class="move-btn-ghost"
            >
              <span class="material-icons text-lg">movie_filter</span>
              Productions
            </router-link>
          </div>
        </div>
      </section>

      <div v-if="loading" class="flex justify-center py-16">
        <div class="login-spinner" aria-label="Loading" />
      </div>

      <div
        v-else-if="error"
        class="rounded-xl px-4 py-3 text-sm border"
        style="background: var(--move-danger-soft); border-color: var(--move-danger); color: var(--move-danger)"
      >
        {{ error }}
      </div>

      <template v-else>
        <section :class="statsGridClass">
          <router-link
            v-for="stat in statsList"
            :key="stat.key"
            :to="stat.to"
            class="move-stat-card hover-lift no-underline"
            :class="`move-stat-card--${stat.variant}`"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="move-stat-card__label">{{ stat.label }}</p>
                <p class="move-stat-card__value">{{ stats[stat.key] }}</p>
              </div>
              <span class="material-icons move-stat-card__icon">{{ stat.icon }}</span>
            </div>
          </router-link>
        </section>

        <section :class="hasProductionAccess ? 'grid grid-cols-1 xl:grid-cols-3 gap-5' : 'grid grid-cols-1 gap-5'">
          <ProductionAttentionList
            v-if="hasProductionAccess"
            class="xl:col-span-1"
            :items="needsAttention"
            :empty-icon="(productionStats.productions || 0) === 0 ? 'theaters' : 'check_circle'"
            :empty-title="productionEmptyTitle"
            :empty-body="productionEmptyBody"
          />

          <section class="move-card overflow-hidden !p-0">
            <div
              class="flex items-center justify-between gap-3 px-5 py-4 border-b"
              style="border-color: var(--move-border)"
            >
              <div>
                <h3 class="text-sm font-bold move-text">Your tasks</h3>
                <p class="text-xs move-text-faint mt-0.5">Due soon first — open a task to keep moving.</p>
              </div>
              <router-link to="/my-tasks" class="text-sm font-semibold shrink-0" style="color: var(--move-accent)">
                View all
              </router-link>
            </div>
            <div v-if="!focusTasks.length" class="move-empty-state py-12 px-5">
              <span class="material-icons">task_alt</span>
              <p class="font-semibold move-text">No tasks assigned to you</p>
              <p class="text-sm move-text-faint mt-1 max-w-sm">Create a task on a board, or wait for one to be assigned.</p>
            </div>
            <ul v-else class="divide-y" style="border-color: var(--move-border)">
              <li v-for="task in focusTasks" :key="task.id">
                <button
                  type="button"
                  class="hub-focus-row"
                  @click="router.push(`/board/${task.boardId}?task=${task.id}`)"
                >
                  <div class="min-w-0 text-left">
                    <p class="font-semibold move-text truncate">{{ task.title }}</p>
                    <p class="text-xs move-text-faint mt-0.5 truncate">{{ task.boardName || 'Board' }}</p>
                  </div>
                  <span
                    class="text-xs font-semibold shrink-0"
                    :class="task.dueState === 'overdue' ? 'hub-due--overdue' : 'move-text-muted'"
                  >
                    {{ formatTaskDue(task.dueDate) }}
                  </span>
                </button>
              </li>
            </ul>
          </section>

          <section v-if="hasProductionAccess" class="move-card overflow-hidden !p-0">
            <div
              class="flex items-center justify-between gap-3 px-5 py-4 border-b"
              style="border-color: var(--move-border)"
            >
              <div>
                <h3 class="text-sm font-bold move-text">Upcoming shoots</h3>
                <p class="text-xs move-text-faint mt-0.5">The next days on the calendar.</p>
              </div>
              <router-link to="/production/productions" class="text-sm font-semibold shrink-0" style="color: var(--move-accent)">
                View all
              </router-link>
            </div>
            <div v-if="!upcomingShoots.length" class="move-empty-state py-12 px-5">
              <span class="material-icons">event</span>
              <p class="font-semibold move-text">No shoot days scheduled</p>
              <p class="text-sm move-text-faint mt-1 max-w-sm">Open a production and set the shoot day so it shows up here.</p>
            </div>
            <ul v-else class="divide-y" style="border-color: var(--move-border)">
              <li v-for="item in upcomingShoots" :key="item.id">
                <router-link
                  :to="`/production/productions/${item.id}/shoot`"
                  class="hub-focus-row no-underline"
                >
                  <div class="min-w-0">
                    <p class="font-semibold move-text truncate">{{ item.name }}</p>
                    <p class="text-xs move-text-faint mt-0.5 truncate">{{ item.project?.name }} · {{ item.shoot_venue || 'Venue TBC' }}</p>
                  </div>
                  <span class="text-xs font-semibold move-text-muted shrink-0">{{ formatDateTime(item.shoot_at) }}</span>
                </router-link>
              </li>
            </ul>
          </section>
        </section>

        <section v-if="hasProductionAccess" class="prod-pipeline">
          <router-link
            v-for="step in productionProgressSteps"
            :key="step.value"
            :to="{ path: '/production/productions', query: { status: step.value } }"
            class="prod-pipeline__item"
          >
            <span class="prod-pipeline__count">{{ byStatus[step.value] || 0 }}</span>
            <span class="prod-pipeline__label">{{ step.short }}</span>
          </router-link>
        </section>

        <section class="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div class="move-card p-5 md:p-6">
            <h3 class="text-base font-bold move-text mb-4">Board distribution</h3>
            <div v-if="hasBoardChartData" class="h-64">
              <Pie :data="boardChartData" :options="pieOptions" />
            </div>
            <div v-else class="move-empty-state h-64">
              <span class="material-icons">view_kanban</span>
              <p class="font-medium move-text">No boards yet</p>
              <p class="text-sm move-text-faint mt-1">Create a board to see distribution here.</p>
            </div>
          </div>

          <div class="move-card p-5 md:p-6">
            <h3 class="text-base font-bold move-text mb-4">Tasks per board</h3>
            <div v-if="hasTaskChartData" class="h-64">
              <Bar :data="taskChartData" :options="chartOptions" />
            </div>
            <div v-else class="move-empty-state h-64">
              <span class="material-icons">bar_chart</span>
              <p class="font-medium move-text">No task data yet</p>
              <p class="text-sm move-text-faint mt-1">Add tasks to your boards to populate this chart.</p>
            </div>
          </div>
        </section>

        <section>
          <h3 class="text-sm font-bold tracking-[0.14em] uppercase move-text-faint mb-3">Quick actions</h3>
          <div
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <button
              v-for="action in quickActions"
              :key="action.label"
              type="button"
              class="move-card p-5 text-left hover-lift active-press group"
              @click="router.push(action.route)"
            >
              <span
                class="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3"
                style="background: var(--move-accent-soft); color: var(--move-accent)"
              >
                <span class="material-icons">{{ action.icon }}</span>
              </span>
              <span class="block font-bold move-text group-hover:text-[var(--move-accent)] transition-colors">
                {{ action.label }}
              </span>
            </button>
          </div>
        </section>
      </template>
    </div>
  </AppLayout>
</template>

<script>
export default {
  name: 'DashboardView'
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.material-icons {
  font-family: 'Material Icons', sans-serif;
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  display: inline-block;
}

.login-spinner {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  border: 3px solid var(--move-accent-soft);
  border-top-color: var(--move-accent);
  animation: dash-spin 0.7s linear infinite;
}

@keyframes dash-spin {
  to { transform: rotate(360deg); }
}

.hover-lift {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.hover-lift:hover {
  transform: translateY(-2px);
}

.active-press:active {
  transform: scale(0.99);
}

.hub-focus-row {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1.25rem;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.hub-focus-row:hover {
  background: var(--move-accent-soft);
}

.hub-due--overdue {
  color: var(--move-danger);
}

.no-underline {
  text-decoration: none;
}
</style>
